import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import React, { ReactNode, useMemo } from 'react';
import { createPalette } from '../foundations/palette';
import { typography } from '../foundations/typography';
import { shadows } from '../foundations/shadows';
import { components } from '../overrides';
import { createDialogOverrides } from '../overrides/Dialog';
import { breakpoints } from '../foundations/breakpoints';
import { shape } from '../foundations/shape';
import {
  useTheme,
  ThemeProvider as CustomThemeProvider,
} from '../context/ThemeContext';

// Create theme based on current mode
export const useAppTheme = () => {
  const { mode } = useTheme();

  // Create a theme instance based on the current mode
  return useMemo(() => {
    const palette = createPalette(mode);
    const dialogOverrides = createDialogOverrides(mode);

    return createTheme({
      palette,
      typography,
      shadows,
      components: {
        ...components,
        ...dialogOverrides,
      },
      shape,
      breakpoints,
    });
  }, [mode]);
};

// MUI Theme Provider
const MuiThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const theme = useAppTheme();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

// Combined theme provider component
export const AppThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  return (
    <CustomThemeProvider>
      <MuiThemeProvider>{children}</MuiThemeProvider>
    </CustomThemeProvider>
  );
};

// Export a default theme for use in places where the context isn't available
export default createTheme({
  palette: createPalette('dark'), // Default to dark mode (Ableton theme)
  typography,
  shadows,
  components: {
    ...components,
    ...createDialogOverrides('dark'),
  },
  shape,
  breakpoints,
});
